import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';

import ActiveFilterChips, { ActiveFilterLabels, describeActiveFilters } from './ActiveFilterChips';
import { fetchFiles } from '../../../services/syntiro_honey/data_ingestion.service';
import { FileListItem, RecordGroupBy } from '../../../types/syntiro_honey';
import ReviewFilterPanel from './ReviewFilterPanel';
import { ReviewFilters } from './useRecordList';

/** How the list is grouped: a grouping dimension, or `none` for the flat grid. */
export type GroupByOption = RecordGroupBy | 'none';

const GROUP_OPTIONS: Array<{ value: GroupByOption; label: string }> = [
  { value: 'metric', label: 'Group by metric' },
  { value: 'division', label: 'Group by entity' },
  { value: 'period', label: 'Group by period' },
  { value: 'none', label: 'No grouping' }
];

const SEARCH_DEBOUNCE_MS = 400;

interface DebouncedTextFilterProps {
  /** Field label. */
  label: string;
  /** Committed filter value (undefined when the filter is off). */
  value: string | undefined;
  /** Called with the trimmed text (or undefined when emptied) after the debounce. */
  onCommit: (value: string | undefined) => void;
}

/**
 * Free-text filter input that commits its trimmed value after a short debounce,
 * so each keystroke does not trigger a server round-trip.
 */
function DebouncedTextFilter({ label, value, onCommit }: DebouncedTextFilterProps) {
  const [draft, setDraft] = useState<string>(value ?? '');

  // Resync the draft when the committed value changes externally (e.g. filter reset).
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  // Commit the trimmed draft once typing pauses; the no-op guard prevents loops.
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = draft.trim();
      const next = trimmed === '' ? undefined : trimmed;
      if (next !== value) {
        onCommit(next);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft, value, onCommit]);

  return (
    <TextField
      label={label}
      size="small"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      sx={{ minWidth: 220, flex: 1 }}
    />
  );
}

interface ReviewFilterBarProps {
  /** The committed filters. */
  filters: ReviewFilters;
  /** Called with the full next filter set on any change (panel resets to page 0). */
  onFiltersChange: (filters: ReviewFilters) => void;
  /** The active grouping dimension. */
  groupBy: GroupByOption;
  /** Called with the newly-chosen grouping dimension. */
  onGroupByChange: (groupBy: GroupByOption) => void;
  /** Re-runs the current query. */
  onRefresh: () => void;
  /** Whether a list request is in flight (disables refresh). */
  loading: boolean;
}

/**
 * The record list's control row: raw-label search, a Filters popover, the grouping
 * selector and refresh — with the active-filter chips directly beneath.
 *
 * This replaced eight permanently-visible controls in one flat wrapping row. Three
 * of those (Status, Mapping, "Learned rules only") duplicated the scope tabs and the
 * summary chips above them, so the same state had two controls in two different
 * visual languages and neither was authoritative. The rest now live grouped in the
 * popover, with every active one named in the chip row so nothing filters invisibly.
 *
 * Search stays on the surface because it is the highest-frequency filter; grouping
 * sits beside Filters as a matching dropdown rather than a third segmented control
 * competing with the page tabs and the scope tabs.
 * @param {ReviewFilterBarProps} props The filters, grouping and refresh wiring.
 * @returns {JSX.Element}
 */
export default function ReviewFilterBar({
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  onRefresh,
  loading
}: ReviewFilterBarProps) {
  const [recentFiles, setRecentFiles] = useState<Array<FileListItem>>([]);
  const [labels, setLabels] = useState<ActiveFilterLabels>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Load the caller's recent uploads once for the file filter; a failure just
  // leaves the dropdown with only the "All files" option.
  useEffect(() => {
    let cancelled = false;
    fetchFiles(50)
      .then((files) => {
        if (!cancelled) {
          setRecentFiles(files);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // The file name is resolvable from the loaded list, so it never needs reporting
  // up from the panel the way the metric and period labels do.
  const activeLabels: ActiveFilterLabels = {
    ...labels,
    fileName: recentFiles.find((file) => file.id === filters.fileId)?.filename
  };
  // The badge answers "is anything hidden in there?" without opening the popover.
  const hiddenFilterCount = describeActiveFilters(filters, activeLabels).filter(
    (descriptor) => descriptor.key !== 'search'
  ).length;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <DebouncedTextFilter
          label="Search raw label"
          value={filters.search}
          onCommit={(search) => onFiltersChange({ ...filters, search })}
        />
        <Badge badgeContent={hiddenFilterCount} color="primary">
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ textTransform: 'none' }}
            aria-label="Filters"
          >
            Filters
          </Button>
        </Badge>
        <TextField
          select
          size="small"
          label="Grouping"
          value={groupBy}
          onChange={(event) => onGroupByChange(event.target.value as GroupByOption)}
          sx={{ minWidth: 180 }}
        >
          {GROUP_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh records">
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            {/* The tooltip is not an accessible name — an icon-only button needs its
                own label or assistive tech announces "button". */}
            <IconButton onClick={onRefresh} disabled={loading} aria-label="Refresh records">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <ActiveFilterChips
        filters={filters}
        labels={activeLabels}
        onFiltersChange={onFiltersChange}
      />
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <ReviewFilterPanel
          filters={filters}
          files={recentFiles}
          metricName={labels.metricName}
          entityName={labels.entityName}
          onFiltersChange={onFiltersChange}
          onLabelChange={(next) => setLabels((current) => ({ ...current, ...next }))}
        />
      </Popover>
    </Stack>
  );
}
